import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { 
  Table, TableContainer, TableHead, TableBody, TextField, FormControl, InputLabel,
  Paper, Tooltip, IconButton, TableSortLabel, Select, MenuItem, Checkbox, ListItemText 
} from '@mui/material'
import { Mail, Refresh, FilterAltOff } from '@mui/icons-material'

import moment from 'moment'

import { D, TRow, TCell } from '../include'
import { ModalSendEmailGroup } from './ModalSendEmailGroup'

moment.locale('pt_br')

const orderKeysMail = [
  { label: 'Usuário', key: 'username' },
  { label: 'Caminho', key: 'url' },
  { label: 'Acesso em', key: 'created_at' },
]

function formatDataToReportMail(data) {
  return data.map(item => ({
    ...item,
    created_at: moment(item.created_at).format('DD/MM/YYYY HH:mm:ss')
  }))
}

export const Logs = ({ api, user, dataInicio, dataFim }) => {
  const [dates, setDates] = useState({
    dataini: moment(dataInicio).format('YYYY-MM-DD'),
    datafim: moment(dataFim).format('YYYY-MM-DD')
  });
  
  const [dataTable, setDataTable] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sortConfig, setSortConfig] = useState({ order: 'asc', orderBy: '' });
  const [showModalSendEmail, setShowModalSendEmail] = useState(false);

  // useRef para garantir que o log de acesso seja feito estritamente UMA vez
  const isFirstRender = useRef(true);

  // Carregamento de Dados
  const updateData = useCallback(async (dts, logar = false) => {
    try {
      const res = await api.get('/logs', { params: { ...dts, logar } });
      setDataTable(res.logs || []);
    } catch (err) {
      console.error('Erro ao atualizar logs:', err);
    }
  }, [api]);

  // Efeito unificado: Controla a montagem inicial e a atualização vindas das Props do pai
  useEffect(() => {
    const newIni = moment(dataInicio).format('YYYY-MM-DD');
    const newFim = moment(dataFim).format('YYYY-MM-DD');
    const newDates = { dataini: newIni, datafim: newFim };

    if (isFirstRender.current) {
      // Primeira vez que o componente aparece na tela -> Fazemos o logar = true
      updateData(newDates, true);
      isFirstRender.current = false;
    } else {
      // Se dataInicio ou dataFim mudarem no componente pai, sincroniza aqui.
      // Como não dependemos mais do estado 'dates' para esse trigger,
      // ele não vai apagar a seleção manual do usuário.
      setDates(newDates);
      updateData(newDates, false); 
    }
    
    // As dependências agora são SÓ as props externas e a função.
    // 'dates' foi REALMENTE removido.
  }, [dataInicio, dataFim, updateData]);


  // Lógica de Negócio Derivada (Memoizada)
  const processedData = useMemo(() => {
    let filtered = [...dataTable];

    // Filtro de permissão
    if (user?.role !== 'admin') {
      filtered = filtered.filter(r => !r.username.toLowerCase().includes('ai2bizlab'));
    }

    // Filtro de usuário selecionado
    const userNames = [...new Set(filtered.map(r => r.username))].sort((a, b) => a.localeCompare(b, 'pt-BR'));
    
    let displayRows = filtered;
    if (selectedUsers.length > 0 && selectedUsers.length !== userNames.length) {
      displayRows = filtered.filter(row => selectedUsers.includes(row.username));
    }

    // Ordenação
    if (sortConfig.orderBy) {
      displayRows.sort((a, b) => {
        const valA = a[sortConfig.orderBy];
        const valB = b[sortConfig.orderBy];
        
        const compare = sortConfig.orderBy === 'created_at' 
          ? new Date(valA) - new Date(valB)
          : String(valA).localeCompare(String(valB), 'pt-BR');
        
        return sortConfig.order === 'asc' ? compare : -compare;
      });
    }

    return { displayRows, userNames };
  }, [dataTable, selectedUsers, sortConfig, user?.role]);

  // 3. Handlers
  const handleDateChange = (newDates) => {
    let { dataini, datafim } = { ...dates, ...newDates };
    if (dataini && datafim && dataini > datafim) {
      if (newDates.dataini) datafim = ''; else dataini = '';
    }
    setDates({ dataini, datafim });
    updateData({ dataini, datafim }, false); // Usuário mexeu no filtro -> logar = false
  };

  const createSortHandler = (property) => {
    setSortConfig(prev => ({
      order: prev.orderBy === property && prev.order === 'asc' ? 'desc' : 'asc',
      orderBy: property
    }));
  };

  const handleUserSelectChange = (event) => {
    const value = event.target.value;
    if (value.includes("__all__") || value.includes("__none__")) return;
    setSelectedUsers(value);
  };

  return (
    <D className="col e aF scD" style={{ padding: '2rem' }}>
      <D className="row w aC">
        <D className="row aC">
          <TextField
            size="small" label="Data inicial" type="date" value={dates.dataini}
            InputLabelProps={{ shrink: true }} inputProps={{ max: dates.datafim }}
            onChange={e => handleDateChange({ dataini: e.target.value })} sx={{ width: '160px' }}
          />
          <D size="1rem" />
          <TextField
            size="small" label="Data final" type="date" value={dates.datafim}
            InputLabelProps={{ shrink: true }} inputProps={{ min: dates.dataini }}
            onChange={e => handleDateChange({ datafim: e.target.value })} sx={{ width: '160px' }}
          />
          <D size="1rem" />
          <FormControl variant="outlined" size="small" sx={{ minWidth: "16em", maxWidth: "46em" }}>
            <InputLabel id="label-users">Selecione Usuários</InputLabel>
            <Select
              labelId="label-users" label="Selecione Usuários" multiple
              value={selectedUsers} onChange={handleUserSelectChange}
              renderValue={(sel) => sel.length === 0 ? "Nenhum" : (sel.length === processedData.userNames.length ? "Todos" : sel.join(", "))}
            >
              <MenuItem value="__all__" onClick={() => setSelectedUsers(processedData.userNames)}>
                <Checkbox checked={selectedUsers.length === processedData.userNames.length && selectedUsers.length > 0} />
                <ListItemText primary="Todos" />
              </MenuItem>
              <MenuItem value="__none__" onClick={() => setSelectedUsers([])}>
                <Checkbox checked={selectedUsers.length === 0} />
                <ListItemText primary="Nenhum" />
              </MenuItem>
              {processedData.userNames.map((name) => (
                <MenuItem key={name} value={name}>
                  <Checkbox checked={selectedUsers.includes(name)} />
                  <ListItemText primary={name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <D size="1rem" />

          <Tooltip title="Limpar filtros" arrow>
            <IconButton 
              onClick={() => { 
                const hoje = moment().format('YYYY-MM-DD');
                const resetDates = { dataini: hoje, datafim: hoje };
                
                setDates(resetDates);
                setSelectedUsers([]);
                // Chama a função de atualização para buscar os logs do dia de hoje
                updateData(resetDates, false); 
              }}
            >
              <FilterAltOff color="action" />
            </IconButton>
          </Tooltip>

          <Tooltip title='Atualizar dados' arrow>
            <span>
              <IconButton onClick={() => updateData(dates, false)} disabled={selectedUsers.length === 0}>
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>
        </D>

        <D style={{ marginLeft: "auto" }}>
          <Tooltip title='Enviar por email' arrow>
            <span>
              <IconButton onClick={() => setShowModalSendEmail(true)} disabled={selectedUsers.length === 0}>
                <Mail />
              </IconButton>
            </span>
          </Tooltip>
        </D>
      </D>

      <D h="1rem" />
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer style={{ height: '100%' }}>
          <Table stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TRow sx={{ height: '2rem', background: '#ccc' }}>
                {['username', 'created_at'].map((col) => (
                  <TCell key={col} sortDirection={sortConfig.orderBy === col ? sortConfig.order : false} sx={{ color: 'white', width: col === 'username' ? '25%' : '15%' }}>
                    <TableSortLabel active={sortConfig.orderBy === col} direction={sortConfig.orderBy === col ? sortConfig.order : 'asc'} onClick={() => createSortHandler(col)} sx={{ color: 'white', '&.Mui-active': { color: 'white' }, '& .MuiTableSortLabel-icon': { color: 'white !important' } }}>
                      {col === 'username' ? 'Usuário' : 'Acesso em'}
                    </TableSortLabel>
                  </TCell>
                ))}
                <TCell sx={{ color: 'white', width: '60%' }}>Caminho</TCell>
              </TRow>
            </TableHead>
            <TableBody>
              {selectedUsers.length === 0 ? (
                <TRow><TCell align="center" colSpan={3}>Selecione um usuário para visualizar</TCell></TRow>
              ) : processedData.displayRows.map((row, i) => (
                <TRow key={i}>
                  <TCell component="th">{row.username}</TCell>
                  <TCell component="th">{moment(row.created_at).format('DD/MM/YYYY HH:mm:ss')}</TCell>
                  <TCell component="th">
                    <Tooltip title={row.url} arrow disableHoverListener={row.url.length <= 100}>
                      <span style={{ display: 'inline-block', maxWidth: '100ch', width: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.url}
                      </span>
                    </Tooltip>
                  </TCell>
                </TRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {showModalSendEmail && (
        <ModalSendEmailGroup
          api={api}
          show={showModalSendEmail}
          onClose={() => setShowModalSendEmail(false)}
          data={dataTable}
          type="logs"
          orderKeys={orderKeysMail}
          functionFormatData={formatDataToReportMail}
          showAlert={alert}
        />
      )}
    </D>
  )
}